import pako from 'pako';
import { URPFileHandler } from './URPFileHandler.js';
import { WaypointExtractor } from './WaypointExtractor.js';
import { WaypointUpdater } from './WaypointUpdater.js';
import { XMLReorderer } from './XMLReorderer.js';
import { NodeCloner } from './NodeCloner.js';
import { changeFeatureForNodes, nodesForWaypoints, findFeatureNodes } from '../services/FeatureChangeService.js';

/**
 * URPParser - Handles parsing and manipulation of Universal Robots .urp files
 * 
 * This is the main facade that composes functionality from focused modules:
 * - URPFileHandler: File reading and decompression
 * - WaypointExtractor: Extracting waypoint data from XML
 * - WaypointUpdater: Updating waypoint position/rotation/joints
 * - XMLReorderer: Reordering waypoints in the XML DOM
 * - NodeCloner: Cloning and creating new waypoints
 * 
 * Architecture:
 * - Decompresses gzipped .urp files
 * - Parses XML structure
 * - Extracts waypoint data (position and rotation)
 * - Provides methods to update waypoints
 */
export class URPParser {
  constructor() {
    this.xmlDoc = null;
    this.rawXML = '';
    this.fileName = '';
    
    // Composed modules
    this.fileHandler = new URPFileHandler();
    this.waypointExtractor = new WaypointExtractor();
    this.waypointUpdater = new WaypointUpdater();
    this.xmlReorderer = new XMLReorderer();
    this.nodeCloner = new NodeCloner();
  }

  /**
   * Parse a .urp file (which is a gzipped XML file)
   * @param {File} file - The .urp file from user input
   * @returns {Promise<Object>} Parsed file data with waypoints
   */
  async parseFile(file) {
    this.fileName = file.name;
    
    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      
      // Use parseData for the actual parsing
      return this.parseData(uint8Array, file.name);
      
    } catch (error) {
      console.error('Error parsing URP file:', error);
      throw new Error(`Failed to parse .urp file: ${error.message}`);
    }
  }

  /**
   * Parse URP data from Uint8Array (for robot downloads or other binary data)
   * @param {Uint8Array} uint8Array - The compressed or uncompressed URP data
   * @param {string} fileName - Optional filename
   * @returns {Object} Parsed data
   */
  parseData(uint8Array, fileName = 'unknown.urp') {
    this.fileName = fileName;
    
    try {
      let decompressed;
      
      // Try to decompress - if it fails, assume it's already uncompressed XML
      try {
        decompressed = pako.inflate(uint8Array, { to: 'string' });
      } catch (decompressError) {
        // If decompression fails, try treating it as plain text XML
        console.log('File not gzipped, treating as plain XML');
        const decoder = new TextDecoder('utf-8');
        decompressed = decoder.decode(uint8Array);
      }
      
      this.rawXML = decompressed;
      
      // Parse XML
      const parser = new DOMParser();
      this.xmlDoc = parser.parseFromString(decompressed, 'text/xml');
      
      // Check for parsing errors
      const parserError = this.xmlDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('XML parsing error: ' + parserError.textContent);
      }
      
      // Extract waypoints using the extractor module
      const waypoints = this.extractWaypoints();
      
      return {
        fileName: this.fileName,
        programName: this.getProgramName(),
        robotSerial: this.getRobotSerial(),
        waypoints: waypoints,
        waypointCount: waypoints.length
      };
      
    } catch (error) {
      console.error('Error parsing URP data:', error);
      throw new Error(`Failed to parse .urp data: ${error.message}`);
    }
  }

  /**
   * Extract all waypoints from the XML document
   * @returns {Array<Object>} Array of waypoint objects
   */
  extractWaypoints() {
    return this.waypointExtractor.extractWaypoints(this.xmlDoc);
  }

  /**
   * Update a waypoint's position
   * @param {number} index - Index of the waypoint to update
   * @param {Object} position - New position {x, y, z}
   */
  updateWaypointPosition(index, position) {
    this.waypointUpdater.updatePosition(this.xmlDoc, index, position);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
  }

  /**
   * Update a waypoint's rotation
   * @param {number} index - Index of the waypoint to update
   * @param {Object} rotation - New rotation {rx, ry, rz}
   */
  updateWaypointRotation(index, rotation) {
    this.waypointUpdater.updateRotation(this.xmlDoc, index, rotation);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
  }

  /**
   * Update a via-point's position
   * @param {number} waypointIndex - Index of the waypoint that has the via-point
   * @param {Object} position - New position {x, y, z}
   */
  updateViaPointPosition(waypointIndex, position) {
    this.waypointUpdater.updateViaPointPosition(this.xmlDoc, waypointIndex, position);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
  }

  /**
   * Update a via-point's rotation
   * @param {number} waypointIndex - Index of the waypoint that has the via-point
   * @param {Object} rotation - New rotation {rx, ry, rz}
   */
  updateViaPointRotation(waypointIndex, rotation) {
    this.waypointUpdater.updateViaPointRotation(this.xmlDoc, waypointIndex, rotation);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
  }

  /**
   * Update a waypoint's feature reference
   * @param {number} index - Index of the waypoint to update
   * @param {string} featureName - New feature name
   */
  updateWaypointFeature(index, featureName) {
    this.waypointUpdater.updateFeature(this.xmlDoc, index, featureName);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
  }

  /**
   * Change the feature referenced by the nodes owning the given waypoints.
   *
   * Node-driven, so it also moves the via-points, Pattern origin/X-direction and
   * SearchPartL touch points those nodes own, plus the Weld/QuickTeach wrapper
   * and its waypoint-less children when the whole group is included. Updating
   * only the way-point poses leaves the rest reading old coordinates against the
   * new frame.
   *
   * @param {Array<number>} waypointIndices - Indices of the waypoints to move
   * @param {Object} toFeature - Target feature {name, position, rotation}
   * @param {Array<Object>} features - All known features, to resolve source frames
   * @param {string} transformMode - 'absolute' or 'relative'
   * @returns {Object} Counts, warnings and the worst world-space drift
   */
  changeFeature(waypointIndices, toFeature, features, transformMode = 'absolute') {
    const poses = this.xmlDoc.querySelectorAll('Pose[key="way-point"]');
    const selected = waypointIndices
      .map((index) => poses[index])
      .filter(Boolean);

    // Converting every waypoint means converting the whole program, including
    // nodes that own no waypoint at all - a SearchPartL sitting directly under
    // MainProgram owns only start-point/contact-point, so walking out from the
    // selected waypoints never reaches it and it would be left behind.
    const isWholeProgram = selected.length === poses.length && poses.length > 0;

    let nodes;
    let partialWrappers = [];
    if (isWholeProgram) {
      nodes = findFeatureNodes(this.xmlDoc);
    } else {
      ({ nodes, partialWrappers } = nodesForWaypoints(this.xmlDoc, selected));

      // Flag pose-bearing nodes a partial selection cannot reason about.
      const included = new Set(nodes);
      findFeatureNodes(this.xmlDoc).forEach((node) => {
        if (included.has(node)) return;
        const ownsWaypoint = node.querySelector(':scope > dataModel > Pose[key="way-point"]');
        const ownsAnyPose = node.querySelector(':scope > dataModel > Pose');
        if (ownsWaypoint || !ownsAnyPose) return;
        const current = node
          .querySelector(':scope > dataModel > data[key="feature"]')
          ?.getAttribute('value');
        if (current && current !== toFeature.name) {
          partialWrappers = partialWrappers.concat([node]);
        }
      });
    }

    const result = changeFeatureForNodes(nodes, toFeature, features, transformMode);

    partialWrappers.forEach((wrapper) => {
      const element = wrapper.querySelector(':scope > dataModel > data[key="feature"]');
      const current = element?.getAttribute('value');
      if (current && current !== toFeature.name) {
        result.warnings.push(
          `${wrapper.getAttribute('strategyProgramNodeType') || 'Wrapper'} node left on `
          + `"${current}" because only some of its waypoints were included.`
        );
      }
    });

    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
    return result;
  }

  /**
   * Update a waypoint's joint positions
   * @param {number} index - Index of the waypoint to update
   * @param {Array<number>} joints - Array of 6 joint angles in radians
   */
  updateWaypointJoints(index, joints) {
    this.waypointUpdater.updateJoints(this.xmlDoc, index, joints);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
  }

  /**
   * Clone an entire parent node with all its child waypoints
   * @param {Element} parentNodeElement - The parent Contributed element to clone
   * @param {Map} waypointUpdates - Map of waypoint index to {position, rotation, viaPoint} updates
   * @returns {Array<number>} Array of indices of the newly created waypoints
   */
  cloneParentNode(parentNodeElement, waypointUpdates) {
    const newIndices = this.nodeCloner.cloneParentNode(this.xmlDoc, parentNodeElement, waypointUpdates);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
    return newIndices;
  }

  /**
   * Add a new waypoint by cloning an existing one
   * @param {number} sourceIndex - Index of waypoint to clone
   * @param {Object} position - New position {x, y, z}
   * @param {Object} rotation - New rotation {rx, ry, rz}
   * @param {Object} options - Optional parameters
   * @param {Object} options.viaPoint - Optional via-point {position, rotation}
   * @param {Array<number>} options.jointPositions - Optional joint positions (6 values in radians)
   * @returns {number} Index of the newly created waypoint
   */
  addWaypoint(sourceIndex, position, rotation, options = {}) {
    const newIndex = this.nodeCloner.addWaypoint(this.xmlDoc, sourceIndex, position, rotation, options);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
    return newIndex;
  }

  /**
   * Reorder waypoints in the XML DOM to match the current state
   * @param {Array<Object>} waypoints - Reordered waypoints array
   */
  reorderWaypointsInXML(waypoints) {
    if (!this.xmlDoc) return;
    
    this.xmlReorderer.reorderWaypoints(this.xmlDoc, waypoints);
    this.rawXML = new XMLSerializer().serializeToString(this.xmlDoc);
  }

  /**
   * Get the current XML as a string
   * @returns {string} XML content
   */
  getXML() {
    return this.rawXML;
  }

  /**
   * Get program name from XML
   * @returns {string} Program name
   */
  getProgramName() {
    if (!this.xmlDoc) return 'Unknown';
    const root = this.xmlDoc.documentElement;
    return root.getAttribute('name') || 'Unknown';
  }

  /**
   * Get robot serial number
   * @returns {string} Robot serial number
   */
  getRobotSerial() {
    if (!this.xmlDoc) return 'Unknown';
    const root = this.xmlDoc.documentElement;
    return root.getAttribute('robotSerialNumber') || 'Unknown';
  }
}
